import axios from 'axios';
import { getApiUrl } from '../config/api';
import { clearCompanyStaffSession } from './companyStaffSession';

const WORKSPACE_ACCESS_KEY = 'workspaceAccessValidTill';
const STAFF_ROLES = new Set(['company_staff', 'company_manager']);

let expiryTimer = null;
let pollTimer = null;
let watcherStarted = false;

export const isStaffScopedUser = (user) => {
  const resolved =
    user ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || 'null');
      } catch {
        return null;
      }
    })();
  return STAFF_ROLES.has(String(resolved?.role || ''));
};

export const setWorkspaceAccessValidTill = (validTill) => {
  if (typeof window === 'undefined') return;
  if (!validTill) {
    localStorage.removeItem(WORKSPACE_ACCESS_KEY);
    return;
  }
  const parsed = new Date(validTill);
  if (Number.isNaN(parsed.getTime())) {
    localStorage.removeItem(WORKSPACE_ACCESS_KEY);
    return;
  }
  localStorage.setItem(WORKSPACE_ACCESS_KEY, parsed.toISOString());
};

export const getWorkspaceAccessValidTill = () => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(WORKSPACE_ACCESS_KEY);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const clearWorkspaceSessionMeta = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(WORKSPACE_ACCESS_KEY);
};

export const logoutWorkspaceExpired = () => {
  if (typeof window === 'undefined') return;

  const path = window.location.pathname || '';
  if (path === '/auth/callback' || path === '/login') {
    clearWorkspaceSessionMeta();
    return;
  }

  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('authRedirectPath');
  clearCompanyStaffSession();
  clearWorkspaceSessionMeta();

  sessionStorage.setItem('workspaceSessionExpired', '1');
  if (path === '/' || path === '') {
    window.location.reload();
    return;
  }
  window.location.replace('/');
};

const scheduleExpiryLogout = () => {
  if (expiryTimer) {
    window.clearTimeout(expiryTimer);
    expiryTimer = null;
  }

  if (!isStaffScopedUser()) {
    clearWorkspaceSessionMeta();
    return;
  }

  const validTill = getWorkspaceAccessValidTill();
  if (!validTill) return;

  const delay = validTill.getTime() - Date.now();
  if (delay <= 0) {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      if (String(stored?.role || '') === 'guest') {
        clearWorkspaceSessionMeta();
        return;
      }
    } catch {
      /* ignore */
    }
    logoutWorkspaceExpired();
    return;
  }

  expiryTimer = window.setTimeout(logoutWorkspaceExpired, delay);
};

const pollWorkspaceSession = async () => {
  const token = localStorage.getItem('token');
  if (!token || !isStaffScopedUser()) return;

  try {
    const response = await axios.get(getApiUrl('/api/auth/me'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    syncWorkspaceAccessFromResponse(response.data);
  } catch (error) {
    const code = error?.response?.data?.code;
    if (code === 'WORKSPACE_SESSION_EXPIRED') {
      logoutWorkspaceExpired();
    }
  }
};

export const syncWorkspaceAccessFromResponse = (payload = {}) => {
  const user = payload.user;
  const access = payload.workspaceAccess;

  if (user && isStaffScopedUser(user)) {
    if (access?.validTill) {
      setWorkspaceAccessValidTill(access.validTill);
    } else {
      clearWorkspaceSessionMeta();
    }
    scheduleExpiryLogout();
    return;
  }

  clearWorkspaceSessionMeta();
  if (expiryTimer) {
    window.clearTimeout(expiryTimer);
    expiryTimer = null;
  }
};

export const startWorkspaceSessionWatcher = () => {
  if (typeof window === 'undefined') return;

  scheduleExpiryLogout();
  if (watcherStarted) return;
  watcherStarted = true;

  pollWorkspaceSession();

  pollTimer = window.setInterval(pollWorkspaceSession, 60 * 1000);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleExpiryLogout();
      pollWorkspaceSession();
    }
  });

  window.addEventListener('codex-authenticated', () => {
    scheduleExpiryLogout();
    pollWorkspaceSession();
  });

  window.addEventListener('workspace-user-updated', scheduleExpiryLogout);
};

export const handleWorkspaceSessionApiError = (error) => {
  const code = error?.response?.data?.code;
  if (code === 'WORKSPACE_SESSION_EXPIRED') {
    logoutWorkspaceExpired();
    return true;
  }
  return false;
};
